import React, { useContext, useState, useRef, useEffect } from "react";
import Link from "../TrailingSlashLink";
import { Dropdown } from "react-bootstrap";
import GlobalContext from "../../context/GlobalContext";
import { useAuth } from "../../context/AuthContext";
import { useUserPlan } from "../../context/UserPlanContext";
import { planTypeFromPlanId } from "../../utils/planUtils";
import ServiceLeadModal from "../ServiceLeadModal/ServiceLeadModal";
import "./TopBar.scss";

const TopBar = () => {
  const gContext = useContext(GlobalContext);
  const { isAuthenticated, user, logout } = useAuth();
  const { plans, planFilters } = useUserPlan();
  const [profileDropdownShow, setProfileDropdownShow] = useState(false);
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);
  const calendlyUrl = process.env.GATSBY_CALENDLY_URL || "https://calendly.com/tenderapex1/30min";
  const closeTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Close dropdown when tapping/clicking outside (needed for mobile touch)
  useEffect(() => {
    const handleOutsideInteraction = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileDropdownShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideInteraction);
    document.addEventListener("touchstart", handleOutsideInteraction);
    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction);
      document.removeEventListener("touchstart", handleOutsideInteraction);
    };
  }, []);

  const handleAuthClick = () => {
    gContext.toggleAuthModal();
  };

  const handleLogout = () => {
    logout();
  };

  const rawName = user?.email || user?.companyname || "User";
  const displayName = rawName.length > 7 ? rawName.slice(0, 7) + "..." : rawName;

  // Determine user plan type for menu filtering
  const getUserPlanType = () => {
    if (!isAuthenticated) {
      return "guest";
    }
    
    const userPlans = (Array.isArray(plans) && plans.length > 0) 
      ? plans 
      : (Array.isArray(user?.plans) && user.plans.length > 0) 
        ? user.plans 
        : [];
    
    if (userPlans.length === 0) {
      return "guest";
    }
    
    const planTypes = userPlans.map(p => {
      const raw = p.plan_type ?? p.plantype ?? null;
      if (raw) return String(raw).toLowerCase().replace(/-/g, "_");
      return planTypeFromPlanId(p.plan_id ?? p.pId ?? p.pid ?? p.planId ?? null);
    }).filter(Boolean);
    
    if (planTypes.length === 0) {
      return "guest";
    }
    
    const hasFreemium = planTypes.includes("freemium");
    const hasEnterprise = planTypes.includes("enterprise") || planTypes.includes("pay_as_you_go");
    const hasInsights = planTypes.includes("insights");
    
    if (hasEnterprise || hasInsights) {
      return "enterprise";
    }
    if (hasFreemium && !hasEnterprise && !hasInsights) {
      return "freemium";
    }
    return "guest";
  };

  // Get filtered menu items based on plan type
  const getProfileMenuItems = () => {
    const planType = getUserPlanType();
    const allMenuItems = [
      { label: "My profile", to: "/me", key: "my-profile" },
      { label: "Change Password", to: "/dashboard-settings", key: "change-password" },
      { label: "Subscription Details", to: "/subscription-details", key: "subscription-details" },
      { label: "Upgrade Plan", to: "/plan-upgrade", key: "upgrade-plan" },
      // { label: "Query Module", to: "/query-listing", key: "query-module" },
      { label: "Log Complaints", to: "/log-complaint", key: "log-complaints" },
      { label: "Make Bidding Profile", to: "/make-bidding-profile", key: "make-bidding-profile" },
      { label: "Generate Template", to: "/generate-template", key: "generate-template" },
      { label: "Feedback", to: "/feedback", key: "feedback" },
    ];

    if (planType === "guest") {
      return allMenuItems.filter(item => 
        ["my-profile", "change-password", "upgrade-plan", "query-module", "log-complaints", "feedback"].includes(item.key)
      );
    } else if (planType === "freemium") {
      return allMenuItems.filter(item => 
        ["my-profile", "change-password", "subscription-details", "upgrade-plan", "query-module", "log-complaints", "feedback"].includes(item.key)
      );
    } else {
      return allMenuItems;
    }
  };

  return (
    <>
      <div className="topbar text-white py-2">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-12">
            <div className="topbar__inner d-flex flex-wrap align-items-center justify-content-center justify-content-lg-between">
              {/* Contact Information */}
              <div className="topbar__contact-wrapper d-flex flex-wrap align-items-center justify-content-center">
                <div className="d-flex align-items-center">
                  <i className="topbar__icon fas fa-phone-alt mr-2"></i>
                  <span className="topbar__label font-weight-bold">Sales:</span>
                  <a href="tel:+919081-155-188" className="py-1 text-white topbar__value ml-1">+91 9081-155-188</a>
                </div>
                
                <div className="d-none d-sm-flex align-items-center topbar__sep-sc">
                  <span className="topbar__separator">|</span>
                </div>
                
                <div className="d-flex align-items-center">
                  <i className="topbar__icon fas fa-headset mr-2"></i>
                  <span className="topbar__label font-weight-bold">Care:</span>
                  <a href="tel:+919925-155-188" className="py-1 text-white topbar__value ml-1">+91 9925-155-188</a>
                </div>
                
                
                
              </div>
              
              {/* Action Buttons */}
              <div className="topbar__buttons-wrapper d-flex align-items-center">
                {!isAuthenticated && (
                  <button
                    type="button"
                    className="btn btn-green topbar__btn-book-demo mx-1 px-4 text-uppercase"
                    onClick={() => setShowBookDemoModal(true)}
                  >
                    Book Demo
                  </button>
                )}
                {isAuthenticated ? (
                  <div
                    ref={dropdownRef}
                    className="position-relative"
                    onMouseEnter={() => {
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = null;
                      }
                      setProfileDropdownShow(true);
                    }}
                    onMouseLeave={() => {
                      closeTimeoutRef.current = setTimeout(() => {
                        setProfileDropdownShow(false);
                      }, 200);
                    }}
                  >
                    <Dropdown
                      className="show-gr-dropdown"
                      show={profileDropdownShow}
                      onToggle={(isOpen) => setProfileDropdownShow(!!isOpen)}
                    >
                      <Dropdown.Toggle
                        as="button"
                        className="d-flex align-items-center border-0 bg-transparent p-0 focus-reset"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileDropdownShow((prev) => !prev);
                        }}
                      >
                        <div
                          className="circle-20 bg-white text-black"
                        >
                          <span className="font-weight-bold">
                            {user?.companyname ? user.companyname.charAt(0).toUpperCase() : (user?.email ? user.email.charAt(0).toUpperCase() : "U")}
                          </span>
                        </div>
                        <i className="fas fa-chevron-down text-white ml-2 font-size-2"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="gr-menu-dropdown dropdown-right bg-white"
                        align="end"
                      >
                      {getProfileMenuItems().map((item) =>
                        item.external ? (
                          <a
                            key={item.label}
                            href={item.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dropdown-item py-1.5 font-size-3 font-weight-semibold line-height-1p2 text-uppercase"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <Link
                            key={item.label}
                            to={item.to}
                            className="dropdown-item py-1.5 font-size-3 font-weight-semibold line-height-1p2 text-uppercase"
                            onClick={() => setProfileDropdownShow(false)}
                          >
                            {item.label}
                          </Link>
                        )
                      )}
                      <button
                        type="button"
                        className="dropdown-item py-1.5 font-size-3 font-weight-semibold line-height-1p2 text-uppercase text-red border-0 bg-transparent w-100 text-left print-hide-logout"
                        onClick={() => {
                          handleLogout();
                          setProfileDropdownShow(false);
                        }}
                      >
                        Logout
                      </button>
                    </Dropdown.Menu>
                  </Dropdown>
                  </div>
                ) : (
                  <button
                    className="btn btn-green mx-1 px-5 text-uppercase"
                    onClick={handleAuthClick}
                  >
                    <i className="topbar__btn-icon fas fa-sign-in-alt mr-1"></i>
                    LOGIN / SIGNUP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <ServiceLeadModal
        show={showBookDemoModal}
        onHide={() => setShowBookDemoModal(false)}
        title="Request a Bid Intelligence Demo"
        page="book-demo"
        calendlyUrl={calendlyUrl}
      />
    </>
  );
};

export default TopBar;
